import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from '../contexts/AuthContext';
// 'storage' import removed as it's no longer used
import { db } from '../firebase'; 
import { collection, addDoc, serverTimestamp, query, getDocs } from 'firebase/firestore';
// Unused firebase/storage imports have been removed
// import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Unused icon imports ('Upload', 'X') have been removed
import { Loader2, AlertTriangle, FileText, DollarSign, Link2 } from 'lucide-react'; 

// --- Minimal Validation Functions ---
const hasCryptoKeywords = (text) => {
    const keywords = [
        'crypto', 'bitcoin', 'btc','eth', 'ethereum', 'wallet', 'address',
        'scam', 'fraud', 'token', 'coin', 'defi', 'nft', 'usdt',
        'usdc', 'binance', 'coinbase', 'phishing', 'rugpull', 'rug pull',
        'ponzi', 'honeypot', 'fake', 'steal', 'stolen', 'hack', 'exploit',
        'blockchain', 'exchange', 'trading', 'investment', 'mining', 'web3',
        'metamask', 'ledger', 'transaction', 'smart contract', 'dapp'
    ];
    const lowerCaseText = text.toLowerCase();
    return keywords.some(keyword => lowerCaseText.includes(keyword));
};

const isNotGibberish = (text) => {
    const sanitized = text.toLowerCase().trim();
    
    // Only check for the most extreme cases
    
    // 1. Reject if it's just the same character repeated
    if (/^(.)\1{4,}$/.test(sanitized.replace(/\s/g, ''))) return false;
    
    // 2. Reject obvious keyboard walks
    if (sanitized.includes('asdfasdfasdf') || 
        sanitized.includes('qwerqwerqwer') || 
        sanitized.includes('zxcvzxcvzxcv')) return false;
    
    // That's it! Much simpler
    return true;
};

// Very minimal quality requirements
const hasMinimumQuality = (title, content) => {
    // Just check they're not empty and have minimal length
    return title.trim().length >= 3 && content.trim().length >= 10;
};

export default function Submit() {
  const [postType, setPostType] = useState('CFE');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  
  // --- MASKED OUT: State for file handling disabled ---
  // const [file, setFile] = useState(null);
  // const [filePreview, setFilePreview] = useState(null);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [fraudMatrixNodes, setFraudMatrixNodes] = useState([]);
  const [fraudMatrixId, setFraudMatrixId] = useState('');
  const [fraudAmount, setFraudAmount] = useState('');
  const [sourceURL, setSourceURL] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    const fetchMatrix = async () => {
      const snapshot = await getDocs(query(collection(db, 'fraudMatrix')));
      setFraudMatrixNodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchMatrix();
  }, []);

  // --- MASKED OUT: File change and removal functions disabled ---
  // const handleFileChange = (e) => {
  //   const selectedFile = e.target.files[0];
  //   if (selectedFile) {
  //     setFile(selectedFile);
  //     setFilePreview(URL.createObjectURL(selectedFile));
  //   }
  // };

  // const removeFile = () => {
  //   setFile(null);
  //   setFilePreview(null);
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (honeypot) { return; }
    if (!recaptchaToken) { return setError('Please complete the reCAPTCHA challenge.'); }
    
    if (!title.trim() || !content.trim()) { return setError('Title and description are required.'); }
    if (!hasMinimumQuality(title, content)) { 
      return setError('Title needs at least 3 characters, description needs at least 10 characters.'); 
    }
    if (!isNotGibberish(title) || !isNotGibberish(content)) { 
      return setError('Please avoid spam-like content.'); 
    }
    
    const combinedText = title + " " + content;
    if (!hasCryptoKeywords(combinedText)) { return setError('Submission must be related to cryptocurrency. Please include relevant crypto-related keywords.'); }
    if (postType === 'CFE' && !fraudMatrixId) { return setError('Please select a fraud category for your CFE report.'); }
    if (!currentUser) { return setError('You must be logged in to post.'); }
    
    setLoading(true);
    
    try {
      // --- MODIFIED: Image upload logic is bypassed ---
      const imageUrl = ''; // Set imageUrl to an empty string directly

      await addDoc(collection(db, 'posts'), {
        type: postType,
        title,
        content,
        imageUrl, // Use the empty imageUrl
        sourceURL,
        fraudMatrixId: postType === 'CFE' ? fraudMatrixId : null,
        fraudAmount: postType === 'CFE' ? Number(fraudAmount) || 0 : 0,
        authorId: currentUser.uid,
        authorEmail: currentUser.email,
        createdAt: serverTimestamp(),
        likes: [],
        verified: false
      });
      navigate('/home');
    } catch (err) {
      setError('Failed to submit post. Please try again.');
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="glass-card rounded-2xl p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Submit a Report</h1>
          <p className="text-gray-400">Please provide as much detail as possible to help the community.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="hidden" aria-hidden="true">
            <label htmlFor="bot-check">Do not fill this out if you are human:</label>
            <input id="bot-check" name="bot-check" type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex="-1" autoComplete="off" />
          </div>

          <div>
            <label className="block font-semibold mb-3 text-gray-200">Report Type</label>
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => setPostType('CFE')} className={`glass-card p-4 rounded-xl border-2 transition-all group ${postType === 'CFE' ? 'border-yellow-500/50 neon-yellow' : 'border-transparent hover:border-yellow-500/30'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-yellow-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white">CFE Report</p>
                    <p className="text-xs text-gray-400">Report fraud or scams</p>
                  </div>
                </div>
              </button>
              
              <button type="button" onClick={() => setPostType('Blog')} className={`glass-card p-4 rounded-xl border-2 transition-all group ${postType === 'Blog' ? 'border-cyan-500/50 neon-cyan' : 'border-transparent hover:border-cyan-500/30'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                    <FileText size={24} className="text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white">Blog Post</p>
                    <p className="text-xs text-gray-400">Share insights & guides</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          {postType === 'CFE' && (
            <div>
              <label htmlFor="fraudCategory" className="block font-semibold mb-3 text-gray-200">Fraud Category *</label>
              <select
                id="fraudCategory"
                value={fraudMatrixId}
                onChange={(e) => setFraudMatrixId(e.target.value)}
                className="w-full glass-card rounded-lg px-4 py-3 text-white bg-black/50 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 [&>option]:bg-gray-900 [&>option]:text-white [&>option:hover]:bg-gray-800"
                required
              >
                <option value="" disabled className="bg-gray-900 text-gray-400">-- Select a category --</option>
                {fraudMatrixNodes.map(node => (
                  <option key={node.id} value={node.id} className="bg-gray-900 text-white">
                    {node.parentId ? `  ↳ ${node.name}` : node.name} ({node.id})
                  </option>
                ))}
                <option value="other" className="bg-gray-900 text-white">Other / Not Listed</option>
              </select>
            </div>
          )}

          <div>
            <label htmlFor="title" className="block font-semibold mb-3 text-gray-200">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              placeholder="Enter a descriptive title..."
              required
            />
          </div>
          
          {postType === 'CFE' && (
            <>
              <div>
                <label htmlFor="sourceURL" className="block font-semibold mb-3 text-gray-200">Source URL (Optional)</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Link2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    id="sourceURL"
                    value={sourceURL}
                    onChange={(e) => setSourceURL(e.target.value)}
                    className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                    placeholder="https://twitter.com/user/status/123"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="fraudAmount" className="block font-semibold mb-3 text-gray-200">Estimated Fraud Amount (USD, Optional)</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="fraudAmount"
                    value={fraudAmount}
                    onChange={(e) => setFraudAmount(e.target.value)}
                    className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                    placeholder="e.g., 1500"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label htmlFor="content" className="block font-semibold mb-3 text-gray-200">
              {postType === 'CFE' ? 'CFE Details (include addresses, websites, etc.) *' : 'Content *'}
            </label>
            <textarea
              id="content"
              rows="8"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              placeholder={postType === 'CFE' ?
                "Provide detailed information about the fraudulent activity, including wallet addresses, transaction hashes, website URLs, and any other relevant evidence..." :
                "Share your insights, analysis, or educational content..."
              }
              required
            />
          </div>
          
          {/* --- MASKED OUT: Image upload section disabled ---
          <div>
            <label className="block font-semibold mb-3 text-gray-200">Evidence & Screenshots (Optional)</label>
            {filePreview ? (
              <div className="relative">
                <img src={filePreview} alt="Preview" className="w-full h-auto max-h-80 object-contain rounded-lg"/>
                <button
                  type="button"
                  onClick={removeFile}
                  className="absolute top-2 right-2 bg-black/50 p-2 rounded-full hover:bg-black/80"
                >
                  <X size={20} className="text-white"/>
                </button>
              </div>
            ) : (
              <div
                className="glass-card rounded-lg p-8 border-2 border-dashed border-gray-600 hover:border-cyan-500/50 transition-colors cursor-pointer text-center"
                onClick={() => document.getElementById('fileInput').click()}
              >
                <input
                  type="file"
                  id="fileInput"
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/png, image/jpeg, image/gif"
                />
                <Upload size={48} className="mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400 mb-1">Drop files here or click to upload</p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
              </div>
            )}
          </div>
          --- END OF MASKED OUT --- */}
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <div className="glass-card rounded-lg p-4 border border-yellow-500/30">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-white">Usage Policy and Warning</h3>
                <div className="mt-2 text-sm text-gray-300 space-y-2">
                  <p>This platform is for reporting legitimate cryptocurrency fraud. Submitting false, misleading, or malicious information constitutes abuse of this system.</p>
                  <p>Accounts found to be in violation of this policy may face consequences, including temporary or permanent suspension.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center">
            <ReCAPTCHA
              sitekey="6LeBBHErAAAAAFW0URBSovL4fRJ3dv9I6onoaXq8"
              onChange={(token) => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
              theme="dark"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`w-full glow-button glass-card py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                postType === 'CFE' ?
                'neon-yellow bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border border-yellow-500/30 hover:from-yellow-500/30 hover:to-orange-500/30' :
                'neon-cyan bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30 hover:from-cyan-500/30 hover:to-blue-500/30'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading && <Loader2 className="animate-spin" size={20} />}
              {loading ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}